import type { AABB, AABB2, AABB3 } from "@novorender/api";
import type { BoundingSphere } from "@novorender/data-js-api";
import { ReadonlyQuat, ReadonlyVec2, ReadonlyVec3, ReadonlyVec4, vec2, vec3 } from "gl-matrix";

/**
 * One sphere enclosing them all, or `undefined` for an empty set.
 *
 * Grows the running sphere towards each one that escapes it, so the answer depends on
 * the order and is not the true minimal enclosing sphere. Every caller frames a camera
 * on it, which is what that approximation is good enough for.
 */
export function mergeBoundingSpheres(spheres: readonly BoundingSphere[]): BoundingSphere | undefined {
    if (spheres.length < 1) {
        return;
    }

    const center = vec3.clone(spheres[0].center);
    let radius = spheres[0].radius;

    for (const sphere of spheres) {
        const delta = vec3.sub(vec3.create(), sphere.center, center);
        const dist = vec3.len(delta) + sphere.radius;

        if (dist > radius) {
            radius = (radius + dist) * 0.5;
            vec3.add(center, center, vec3.scale(delta, delta, 1 - radius / dist));
        }
    }

    return { center, radius };
}

/** One axis-aligned box enclosing them all, or `undefined` for an empty set. */
export function unionAABB3(boxes: readonly AABB[]): AABB | undefined {
    if (boxes.length < 1) {
        return;
    }

    const min = vec3.clone(boxes[0].min);
    const max = vec3.clone(boxes[0].max);

    for (const box of boxes) {
        vec3.min(min, min, box.min);
        vec3.max(max, max, box.max);
    }

    return { min, max };
}

export type Line3D = {
    start: ReadonlyVec3;
    end: ReadonlyVec3;
};

/**
 * Project point on a line segment.
 * E.g. here we project point C on line segment AB and D is the result.
 *    C
 *    |
 * A--D-----B
 * @param out output point
 * @param p point
 * @param l1 first line segment point
 * @param l2 second line segment point
 * @returns point or (if outside the segment) first or last point based on side
 */
export function closestPointOnLine(
    out: vec2,
    p: ReadonlyVec2,
    l1: ReadonlyVec2,
    l2: ReadonlyVec2,
): { point: vec2; parameter: number } {
    const lVec = vec2.sub(vec2.create(), l1, l2);
    const pVec = vec2.sub(vec2.create(), l1, p);
    const offset = (vec2.len(lVec) * vec2.dot(lVec, pVec)) / vec2.dot(lVec, lVec);
    const k = offset / vec2.len(lVec);
    if (k < 0) {
        return { point: l1, parameter: 0 };
    }
    if (k > 1) {
        return { point: l2, parameter: 1 };
    }
    return {
        point: vec2.lerp(out, l1, l2, k),
        parameter: k,
    };
}

export function radToDeg(radian: number) {
    return (radian / Math.PI) * 180;
}

export function degToRad(deg: number) {
    return (deg / 180) * Math.PI;
}

export function pointToLineDistance(p: ReadonlyVec3, l1: ReadonlyVec3, l2: ReadonlyVec3) {
    const line = vec3.subtract(vec3.create(), l2, l1);
    const lineLengthSquared = vec3.sqrLen(line);
    if (lineLengthSquared === 0) {
        return vec3.dist(p, l1);
    }
    const t = Math.max(0, Math.min(1, vec3.dot(vec3.subtract(vec3.create(), p, l1), line) / lineLengthSquared));
    const projection = vec3.scaleAndAdd(vec3.create(), l1, line, t);
    return vec3.dist(p, projection);
}

export function pointToPlaneDistance(p: ReadonlyVec3, normalOffset: ReadonlyVec4) {
    const [x0, y0, z0] = p;
    const [a, b, c] = normalOffset;
    const d = -normalOffset[3];
    return Math.abs(a * x0 + b * y0 + c * z0 + d) / Math.sqrt(a * a + b * b + c * c);
}

export function projectPointOntoPlane(point: ReadonlyVec3, planeNormal: ReadonlyVec3, planePoint: ReadonlyVec3): vec3 {
    const planeToPoint = vec3.subtract(vec3.create(), point, planePoint);
    const d = vec3.dot(planeToPoint, planeNormal);
    const projection = vec3.scaleAndAdd(vec3.create(), point, planeNormal, -d);
    return projection;
}

export function decomposeNormalOffset(normalOffset: ReadonlyVec4) {
    const normal = vec3.fromValues(normalOffset[0], normalOffset[1], normalOffset[2]);
    const point = vec3.create();
    vec3.scaleAndAdd(point, point, normal, normalOffset[3]);
    return { normal, point };
}

export function pointToRectDistance(point: ReadonlyVec2, rect: AABB2) {
    if (point[0] >= rect.min[0] && point[0] <= rect.max[0] && point[1] >= rect.min[1] && point[1] <= rect.max[1]) {
        return 0;
    }

    if (point[0] >= rect.min[0] && point[0] < rect.max[0]) {
        return point[1] < rect.min[1] ? rect.min[1] - point[1] : point[1] - rect.max[1];
    }

    if (point[1] >= rect.min[1] && point[1] < rect.max[1]) {
        return point[0] < rect.min[0] ? rect.min[0] - point[0] : point[0] - rect.max[0];
    }

    if (point[0] < rect.min[0]) {
        if (point[1] < rect.min[1]) {
            return vec2.dist(point, rect.min);
        } else {
            return vec2.dist(point, vec2.fromValues(rect.min[0], rect.max[1]));
        }
    } else {
        if (point[1] < rect.min[1]) {
            return vec2.dist(point, vec2.fromValues(rect.max[0], rect.min[1]));
        } else {
            return vec2.dist(point, rect.max);
        }
    }
}

export function isRectInsideCircle(rect: AABB2, center: ReadonlyVec2, radius: number) {
    return [
        rect.min,
        vec2.fromValues(rect.min[0], rect.max[1]),
        vec2.fromValues(rect.max[0], rect.min[1]),
        rect.max,
    ].every((p) => vec2.dist(center, p) <= radius);
}

export function getPerpendicular(normal: ReadonlyVec3) {
    // Choose a vector that is not parallel to the normal vector
    let v = vec3.fromValues(0, 0, 1);
    if (Math.abs(vec3.dot(v, normal)) > 0.98) {
        v = vec3.fromValues(0, 1, 0);
    }
    // Compute a vector that is perpendicular to both normal and v
    const perpendicularVector = vec3.cross(vec3.create(), normal, v);
    // Normalize the perpendicular vector
    return vec3.normalize(perpendicularVector, perpendicularVector);
}

export function getSignedRotationFromQuat(q: ReadonlyQuat, normal: ReadonlyVec3): number {
    const rotationAxis = vec3.fromValues(q[0], q[1], q[2]);
    const angle = 2 * Math.acos(q[3]);
    if (vec3.dot(rotationAxis, normal) < 0) {
        return -angle;
    }
    return angle;
}

export function addPointToAABB3(aabb: AABB3, point: ReadonlyVec3) {
    for (let i = 0; i < 3; ++i) {
        aabb.max[i] = Math.max(aabb.max[i], point[i]);
        aabb.min[i] = Math.min(aabb.min[i], point[i]);
    }
}

export function addAABB3ToAABB3(aabb: AABB3, added: AABB3) {
    vec3.min(aabb.min, aabb.min, added.min);
    vec3.max(aabb.max, aabb.max, added.max);
}

/** Axis-aligned cube that encloses a sphere. */
export function sphereToAABB3(center: ReadonlyVec3, radius: number): AABB3 {
    return {
        min: vec3.fromValues(center[0] - radius, center[1] - radius, center[2] - radius),
        max: vec3.fromValues(center[0] + radius, center[1] + radius, center[2] + radius),
    };
}

function isFiniteVec3(v: ArrayLike<number>): boolean {
    return Number.isFinite(v[0]) && Number.isFinite(v[1]) && Number.isFinite(v[2]);
}

/** True when every min/max component is a real number — scene aabbs can be ±Infinity. */
export function isFiniteAabb3(box: { min: ArrayLike<number>; max: ArrayLike<number> }): boolean {
    return isFiniteVec3(box.min) && isFiniteVec3(box.max);
}

/** Axis-aligned cube around a finite sphere, or nothing if center/radius are not real. */
export function finiteSphereToAabb(sphere?: { center: ReadonlyVec3; radius: number }): AABB3 | undefined {
    if (!sphere || !Number.isFinite(sphere.radius) || !isFiniteVec3(sphere.center)) {
        return undefined;
    }
    return sphereToAABB3(sphere.center, sphere.radius);
}

/**
 * The scene's own finite box, or a cube around its bounding sphere. `config.aabb` can contain
 * ±Infinity / NaN (see orthoCam/utils.ts); those must not be handed to a driver as model extents.
 */
export function sceneVolumeAabb(config?: {
    aabb?: AABB3;
    boundingSphere?: { center: ReadonlyVec3; radius: number };
}): AABB3 | undefined {
    if (config?.aabb && isFiniteAabb3(config.aabb)) {
        return config.aabb;
    }
    return finiteSphereToAabb(config?.boundingSphere);
}

export function intersectLineWithPlane(
    lineStart: ReadonlyVec3,
    lineEnd: ReadonlyVec3,
    planeNormal: ReadonlyVec3,
    coplanarPoint: ReadonlyVec3,
): ReadonlyVec3 | null {
    const lineDir = vec3.sub(vec3.create(), lineEnd, lineStart);
    const denominator = vec3.dot(lineDir, planeNormal);
    const planeOffset = -vec3.dot(coplanarPoint, planeNormal);
    if (denominator === 0) {
        if (vec3.dot(lineStart, planeNormal) + planeOffset === 0) {
            return lineStart;
        }

        return null;
    }

    const t = -(vec3.dot(lineStart, planeNormal) + planeOffset) / denominator;
    if (t < 0 || t > 1) {
        return null;
    }
    return vec3.scaleAndAdd(vec3.create(), lineStart, lineDir, t);
}

export function intersectRayWithPlane(rayOrigin: vec3, rayDir: vec3, planeNormal: vec3, planeOffset: number) {
    // dot(n, v)
    const ndotv = vec3.dot(planeNormal, rayDir);
    if (Math.abs(ndotv) < 1e-6) {
        // No intersection (ray is parallel to the plane)
        return null;
    }

    // dot(n, P0)
    const ndotP0 = vec3.dot(planeNormal, rayOrigin);

    // t = -(n · P0 + d) / (n · v)
    const t = -(ndotP0 + planeOffset) / ndotv;

    // Calculate intersection point: P0 + v * t
    return vec3.scaleAndAdd(vec3.create(), rayOrigin, rayDir, t);
}

export function pointToLineDistance2D([px, py]: ReadonlyVec2, [lx1, ly1]: ReadonlyVec2, [lx2, ly2]: ReadonlyVec2) {
    const xd = lx2 - lx1;
    const yd = ly2 - ly1;
    if (xd === 0 && yd === 0) {
        return Number.MAX_SAFE_INTEGER;
    }
    return Math.abs((lx2 - lx1) * (ly1 - py) - (lx1 - px) * (ly2 - ly1)) / Math.sqrt(xd * xd + yd * yd);
}

export function isPointCloseToLine2D(v0: ReadonlyVec2, v1: ReadonlyVec2, v2: ReadonlyVec2, sqrSensitivity: number) {
    // Close to first point
    if (vec2.sqrDist(v0, v1) <= sqrSensitivity) {
        return true;
    }

    // Close to second point
    if (vec2.sqrDist(v0, v2) <= sqrSensitivity) {
        return true;
    }

    // Is in line segment reach
    const maxSqrDistance = vec2.sqrDist(v1, v2) + sqrSensitivity;
    if (vec2.sqrDist(v0, v1) > maxSqrDistance || vec2.sqrDist(v0, v2) > maxSqrDistance) {
        return false;
    }

    // Is close to line segment
    const distance = pointToLineDistance2D(v0, v1, v2);
    if (distance * distance <= sqrSensitivity) {
        return true;
    }

    return false;
}

export function lineSegmentsIntersects2D(a1: ReadonlyVec2, a2: ReadonlyVec2, b1: ReadonlyVec2, b2: ReadonlyVec2) {
    const det = (a2[0] - a1[0]) * (b2[1] - b1[1]) - (a2[1] - a1[1]) * (b2[0] - b1[0]);
    if (det === 0) {
        return false; // Lines are parallel
    }

    const lambda = ((b2[0] - b1[0]) * (a1[1] - b1[1]) - (b2[1] - b1[1]) * (a1[0] - b1[0])) / det;
    const gamma = ((a2[0] - a1[0]) * (a1[1] - b1[1]) - (a2[1] - a1[1]) * (a1[0] - b1[0])) / det;

    return lambda >= 0 && lambda <= 1 && gamma >= 0 && gamma <= 1;
}

export function lineSegmentIntersectsAabb2(l1: ReadonlyVec2, l2: ReadonlyVec2, rect: AABB2) {
    return (
        lineSegmentsIntersects2D(l1, l2, rect.min, vec2.fromValues(rect.max[0], rect.min[1])) ||
        lineSegmentsIntersects2D(l1, l2, vec2.fromValues(rect.max[0], rect.min[1]), rect.max) ||
        lineSegmentsIntersects2D(l1, l2, rect.min, vec2.fromValues(rect.min[0], rect.max[1])) ||
        lineSegmentsIntersects2D(l1, l2, vec2.fromValues(rect.min[0], rect.max[1]), rect.max)
    );
}

export function clamp(value: number, min: number, max: number) {
    return Math.max(min, Math.min(max, value));
}

export function isPointInAabb2(aabb: AABB2, p: vec2, sensitivity: number) {
    return (
        p[0] >= aabb.min[0] - sensitivity &&
        p[0] <= aabb.max[0] + sensitivity &&
        p[1] >= aabb.min[1] - sensitivity &&
        p[1] <= aabb.max[1] + sensitivity
    );
}

/**
 * Get the slope of the line defined by two points.
 * Slope is defined as the ratio of the vertical distance to the planar distance, multiplied by 100.
 * @param v1 The first point.
 * @param v2 The second point.
 * @returns The slope of the line in percent, but can exceed 0. If planar distance is 0, returns Infinity.
 */
export function getLineSlope(v1: ReadonlyVec3, v2: ReadonlyVec3): number {
    const zDist = Math.abs(v1[2] - v2[2]);
    const planarDist = vec2.dist([v1[0], v1[1]], [v2[0], v2[1]]);
    if (planarDist === 0) {
        return Infinity;
    }
    return Math.abs(zDist / planarDist) * 100;
}

const zeroVec: ReadonlyVec3 = vec3.create();
export function getLineSegmentLength2d(v1: ReadonlyVec3, v2: ReadonlyVec3, planeNormal: ReadonlyVec3) {
    const p1 = projectPointOntoPlane(v1, planeNormal, zeroVec);
    const p2 = projectPointOntoPlane(v2, planeNormal, zeroVec);
    return vec3.distance(p1, p2);
}

export function isPoint2dInsidePolygon(x: number, y: number, polygon: ReadonlyVec2[]) {
    // ray-casting algorithm based on
    // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html

    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i][0],
            yi = polygon[i][1];
        const xj = polygon[j][0],
            yj = polygon[j][1];

        const intersect = yi > y != yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
        if (intersect) {
            inside = !inside;
        }
    }

    return inside;
}

/**
 * Get the parameter t for the closest point on the infinite line to the infinite line defined by the ray.
 * Both the ray and the line are treated as infinite lines (no s >= 0 constraint is applied).
 * @param rayOrigin The origin of the line defined by the ray (treated as an infinite line).
 * @param rayDir The direction of the line defined by the ray (treated as an infinite line).
 * @param lineOrigin The origin of the other infinite line.
 * @param lineDir The direction of the other infinite line.
 * @param scratch Optional pre-allocated scratch vectors to avoid per-call heap allocation in hot paths.
 * @returns The parameter t for the closest point on the line `lineOrigin + t * lineDir` to the line defined by the ray. Returns null when the ray and line directions are (near) parallel.
 */
export function getRayLineClosestParam(
    rayOrigin: ReadonlyVec3,
    rayDir: ReadonlyVec3,
    lineOrigin: ReadonlyVec3,
    lineDir: ReadonlyVec3,
    scratch?: { crossDN: vec3; diff: vec3; crossDDiff: vec3 },
): number | null {
    const crossDN = vec3.cross(scratch?.crossDN ?? vec3.create(), rayDir, lineDir);
    const denom = vec3.sqrLen(crossDN);
    if (denom < 1e-12) {
        return null;
    }
    const diff = vec3.sub(scratch?.diff ?? vec3.create(), rayOrigin, lineOrigin);
    const crossDDiff = vec3.cross(scratch?.crossDDiff ?? vec3.create(), rayDir, diff);
    return vec3.dot(crossDDiff, crossDN) / denom;
}

/**
 * Returns true when `point` is on the clipped-away side of a single clipping plane.
 *
 * A clipping plane is encoded as `normalOffset = (nx, ny, nz, w)` where `(nx, ny, nz)` is
 * the unit normal and the plane passes through `w * n`. Geometry with
 * `dot(position, n) > w` is clipped away.
 */
export function isCameraOnClippedSide(point: ReadonlyVec3, normalOffset: ReadonlyVec4): boolean {
    const [nx, ny, nz, w] = normalOffset;
    return nx * point[0] + ny * point[1] + nz * point[2] > w;
}
